import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { resourceIntakeRequestsApi } from '@/utils/api';
import type { ResourceIntakeRequest, RequestStatus, ValidationResult, BudgetImpactResult } from '@/types';
import type { RootState } from '../index';

interface ResourceIntakeRequestsState {
  entities: ResourceIntakeRequest[];
  currentEntity: ResourceIntakeRequest | null;
  filters: {
    status: RequestStatus | null;
  };
  loading: boolean;
  error: string | null;
  // Validation state
  validationResult: ValidationResult | null;
  validationLoading: boolean;
  // Budget impact state
  budgetImpactReports: BudgetImpactResult[];
  budgetImpactLoading: boolean;
}

const initialState: ResourceIntakeRequestsState = {
  entities: [],
  currentEntity: null,
  filters: {
    status: null,
  },
  loading: false,
  error: null,
  // Validation state
  validationResult: null,
  validationLoading: false,
  // Budget impact state
  budgetImpactReports: [],
  budgetImpactLoading: false,
};

// Async thunks
export const fetchRequests = createAsyncThunk(
  'resourceIntakeRequests/fetchAll',
  async (params: { status?: RequestStatus } | undefined, { rejectWithValue }) => {
    try {
      const response = await resourceIntakeRequestsApi.getAll(params);

      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch requests');
      }
      return (response.data as { requests: ResourceIntakeRequest[] })?.requests || [];
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch requests');
    }
  }
);

export const fetchRequestById = createAsyncThunk(
  'resourceIntakeRequests/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await resourceIntakeRequestsApi.getById(id);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch request');
      }
      return (response.data as { request: ResourceIntakeRequest })?.request || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch request');
    }
  }
);

export const createRequest = createAsyncThunk(
  'resourceIntakeRequests/create',
  async (data: Partial<ResourceIntakeRequest>, { rejectWithValue }) => {
    try {
      const response = await resourceIntakeRequestsApi.create(data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to create request');
      }
      return (response.data as { request: ResourceIntakeRequest })?.request || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to create request');
    }
  }
);

export const updateRequest = createAsyncThunk(
  'resourceIntakeRequests/update',
  async ({ id, data }: { id: string; data: Partial<ResourceIntakeRequest> }, { rejectWithValue }) => {
    try {
      const response = await resourceIntakeRequestsApi.update(id, data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to update request');
      }
      return (response.data as { request: ResourceIntakeRequest })?.request || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to update request');
    }
  }
);

// Special thunks for approval workflow
export const approveRequest = createAsyncThunk(
  'resourceIntakeRequests/approve',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await resourceIntakeRequestsApi.approve(id);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to approve request');
      }
      return (response.data as { request: ResourceIntakeRequest })?.request || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to approve request');
    }
  }
);

export const declineRequest = createAsyncThunk(
  'resourceIntakeRequests/decline',
  async ({ id, reason }: { id: string; reason?: string }, { rejectWithValue }) => {
    try {
      const response = await resourceIntakeRequestsApi.decline(id, reason);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to decline request');
      }
      return (response.data as { request: ResourceIntakeRequest })?.request || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to decline request');
    }
  }
);

// Validation thunk - validate without saving
export const validateRequest = createAsyncThunk(
  'resourceIntakeRequests/validate',
  async (
    data: {
      resourceLevel?: string;
      resourceLevelId?: string;
      programId?: string;
      resourceType?: string;
      businessJustification?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await resourceIntakeRequestsApi.validate(data);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Validation failed');
      }
      return (response.data as { validation: ValidationResult })?.validation || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Validation failed');
    }
  }
);

// Budget impact thunk - get budget impact report
export const fetchBudgetImpact = createAsyncThunk(
  'resourceIntakeRequests/fetchBudgetImpact',
  async (programId?: string, { rejectWithValue }) => {
    try {
      const response = await resourceIntakeRequestsApi.getBudgetImpact(programId);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to fetch budget impact');
      }
      // Response is either { report: BudgetImpactResult } for single program
      // or { reports: BudgetImpactResult[] } for all programs
      const data = response.data as { report?: BudgetImpactResult; reports?: BudgetImpactResult[] };
      if (data.report) {
        return [data.report];
      }
      return data.reports || [];
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch budget impact');
    }
  }
);

// Enhanced approve with budget override option
export const approveRequestWithOverride = createAsyncThunk(
  'resourceIntakeRequests/approveWithOverride',
  async ({ id, overrideBudgetWarning }: { id: string; overrideBudgetWarning?: boolean }, { rejectWithValue }) => {
    try {
      const response = await resourceIntakeRequestsApi.approve(id, overrideBudgetWarning);
      if (!response.success) {
        return rejectWithValue(response.error?.message || 'Failed to approve request');
      }
      return (response.data as { request: ResourceIntakeRequest; budgetImpact?: BudgetImpactResult })?.request || null;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to approve request');
    }
  }
);

const resourceIntakeRequestsSlice = createSlice({
  name: 'resourceIntakeRequests',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentEntity: (state, action: PayloadAction<ResourceIntakeRequest | null>) => {
      state.currentEntity = action.payload;
    },
    setStatusFilter: (state, action: PayloadAction<RequestStatus | null>) => {
      state.filters.status = action.payload;
    },
    clearFilters: (state) => {
      state.filters = {
        status: null,
      };
    },
    clearValidation: (state) => {
      state.validationResult = null;
      state.validationLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = action.payload;
      })
      .addCase(fetchRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch by ID
      .addCase(fetchRequestById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRequestById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentEntity = action.payload;
      })
      .addCase(fetchRequestById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create
      .addCase(createRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createRequest.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.entities.unshift(action.payload);
        }
      })
      .addCase(createRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update
      .addCase(updateRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRequest.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const index = state.entities.findIndex(r => r.id === action.payload!.id);
          if (index !== -1) {
            state.entities[index] = action.payload;
          }
          if (state.currentEntity?.id === action.payload.id) {
            state.currentEntity = action.payload;
          }
        }
      })
      .addCase(updateRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Approve request
      .addCase(approveRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveRequest.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const index = state.entities.findIndex(r => r.id === action.payload!.id);
          if (index !== -1) {
            state.entities[index] = action.payload;
          }
          if (state.currentEntity?.id === action.payload.id) {
            state.currentEntity = action.payload;
          }
        }
      })
      .addCase(approveRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Decline request
      .addCase(declineRequest.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(declineRequest.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const index = state.entities.findIndex(r => r.id === action.payload!.id);
          if (index !== -1) {
            state.entities[index] = action.payload;
          }
          if (state.currentEntity?.id === action.payload.id) {
            state.currentEntity = action.payload;
          }
        }
      })
      .addCase(declineRequest.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Validate request (without saving)
      .addCase(validateRequest.pending, (state) => {
        state.validationLoading = true;
        state.validationResult = null;
      })
      .addCase(validateRequest.fulfilled, (state, action) => {
        state.validationLoading = false;
        state.validationResult = action.payload;
      })
      .addCase(validateRequest.rejected, (state, action) => {
        state.validationLoading = false;
        state.error = action.payload as string;
      })
      // Fetch budget impact
      .addCase(fetchBudgetImpact.pending, (state) => {
        state.budgetImpactLoading = true;
      })
      .addCase(fetchBudgetImpact.fulfilled, (state, action) => {
        state.budgetImpactLoading = false;
        state.budgetImpactReports = action.payload;
      })
      .addCase(fetchBudgetImpact.rejected, (state, action) => {
        state.budgetImpactLoading = false;
        state.error = action.payload as string;
      })
      // Approve with override
      .addCase(approveRequestWithOverride.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(approveRequestWithOverride.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const index = state.entities.findIndex(r => r.id === action.payload!.id);
          if (index !== -1) {
            state.entities[index] = action.payload;
          }
          if (state.currentEntity?.id === action.payload.id) {
            state.currentEntity = action.payload;
          }
        }
      })
      .addCase(approveRequestWithOverride.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setCurrentEntity, setStatusFilter, clearFilters, clearValidation } =
  resourceIntakeRequestsSlice.actions;

// Selectors
export const selectAllRequests = (state: RootState) => state.resourceIntakeRequests.entities;
export const selectRequestsByStatus = (state: RootState, status: RequestStatus) =>
  state.resourceIntakeRequests.entities.filter(r => r.status === status);
export const selectMyRequests = (state: RootState, userId: string) =>
  state.resourceIntakeRequests.entities.filter(r => r.requestedById === userId);
export const selectPendingRequests = (state: RootState) =>
  state.resourceIntakeRequests.entities.filter(r => r.status === 'SUBMITTED');
export const selectRequestsLoading = (state: RootState) => state.resourceIntakeRequests.loading;
export const selectRequestsError = (state: RootState) => state.resourceIntakeRequests.error;
export const selectCurrentRequest = (state: RootState) => state.resourceIntakeRequests.currentEntity;
export const selectRequestFilters = (state: RootState) => state.resourceIntakeRequests.filters;

// Validation selectors
export const selectValidationResult = (state: RootState) => state.resourceIntakeRequests.validationResult;
export const selectValidationLoading = (state: RootState) => state.resourceIntakeRequests.validationLoading;

// Budget impact selectors
export const selectBudgetImpactReports = (state: RootState) => state.resourceIntakeRequests.budgetImpactReports;
export const selectBudgetImpactLoading = (state: RootState) => state.resourceIntakeRequests.budgetImpactLoading;
export const selectBudgetImpactByProgram = (state: RootState, programId: string) =>
  state.resourceIntakeRequests.budgetImpactReports.find(r => r.programId === programId);

export default resourceIntakeRequestsSlice.reducer;
